import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { onSnapshot, doc } from "firebase/firestore";
import { signOut } from "firebase/auth";

function Navbar() {
  const navigate = useNavigate();
  const [points, setPoints] = useState(0);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      if (u) {
        const unsubDoc = onSnapshot(doc(db, "users", u.uid), (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setPoints(data.points || 0);
            setAvatar(data.profileImage || "");
          }
        });
        return () => unsubDoc();
      } else {
        setPoints(0);
      }
    });

    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);

    return () => unsubAuth();
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.clear();
    navigate("/");
  };

  const navTo = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <nav className="glass-morphism" style={styles.navbar}>
      <div className="app-container" style={styles.container}>
        {/* LOGO */}
        <h2 className="gradient-text" style={styles.logo} onClick={() => navTo("/home")}>
          IPL ARENA
        </h2>

        {/* CONTROLS AREA */}
        <div style={styles.controls}>
          <button onClick={toggleTheme} style={styles.themeBtn} title="Toggle Theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          <div style={{ ...styles.pointsBadge, display: window.innerWidth < 768 ? 'none' : 'flex' }} className="desktop-only">
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.6rem', fontWeight: '800' }}>POINTS</span>
            <span style={{ fontWeight: '900', color: 'var(--primary)', fontSize: '1rem' }}>{points}</span>
          </div>

          <button onClick={toggleMenu} style={styles.hamburger} className="mobile-only">
            {isMenuOpen ? "✕" : "☰"}
          </button>

          {/* DESKTOP LINKS */}
          <div style={styles.desktopLinks} className="desktop-only">
            <button style={styles.link} onClick={() => navTo("/home")}>MATCHES</button>
            <button style={styles.link} onClick={() => navTo("/mypredictions")}>MY PICKS</button>
            <button style={styles.link} onClick={() => navTo("/leaderboard")}>RANKINGS</button>
            <button style={styles.link} onClick={() => navTo("/how-to-play")}>HOW TO PLAY</button>
            <button style={styles.link} onClick={() => navTo("/feedback")}>FEEDBACK</button>
            {user && (
              <div style={styles.profileBox} onClick={() => navTo("/profile")}>
                {avatar ? (
                  <img src={avatar} alt="P" style={styles.navAvatar} />
                ) : (
                  <div style={styles.avatarFallback}>{user.email.charAt(0).toUpperCase()}</div>
                )}
              </div>
            )}
            {user && <button onClick={handleLogout} style={styles.logoutBtn}>LOGOUT</button>}
          </div>
        </div>

        {/* MOBILE MENU OVERLAY */}
        {isMenuOpen && (
          <div className="glass-morphism animate-fade-in" style={styles.mobileMenu}>
            <button style={styles.mobileLink} onClick={() => navTo("/home")}>MATCHES</button>
            <button style={styles.mobileLink} onClick={() => navTo("/mypredictions")}>MY PICKS</button>
            <button style={styles.mobileLink} onClick={() => navTo("/leaderboard")}>RANKINGS</button>
            <button style={styles.mobileLink} onClick={() => navTo("/how-to-play")}>HOW TO PLAY</button>
            <button style={styles.mobileLink} onClick={() => navTo("/feedback")}>FEEDBACK</button>
            {user && <button style={styles.mobileLink} onClick={() => navTo("/profile")}>MY PROFILE ({points} PTS)</button>}
            {user && <button onClick={handleLogout} style={{ ...styles.mobileLink, color: '#ef4444' }}>SIGN OUT</button>}
          </div>
        )}
      </div>
    </nav>
  );
}

const styles = {
  navbar: { position: "sticky", top: 0, zIndex: 1000, padding: "0.8rem 0", marginBottom: "1.5rem" },
  container: { display: "flex", justifyContent: "space-between", alignItems: "center", position: 'relative' },
  logo: { cursor: "pointer", fontSize: '1.4rem', fontWeight: '900', zIndex: 1001 },
  controls: { display: 'flex', alignItems: 'center', gap: '1rem' },
  themeBtn: { background: 'var(--glass)', border: '1px solid var(--glass-border)', fontSize: '1.2rem', padding: '0.4rem', borderRadius: '12px', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pointsBadge: { flexDirection: 'column', alignItems: 'center', background: 'var(--glass)', border: '1px solid var(--glass-border)', padding: '0.3rem 0.8rem', borderRadius: '12px', minWidth: '60px' },
  hamburger: { color: 'var(--text-primary)', fontSize: '1.5rem', cursor: 'pointer' },
  desktopLinks: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  link: { color: "var(--text-secondary)", fontSize: "0.85rem", fontWeight: "700", padding: "0.5rem 0.8rem", borderRadius: "10px" },
  mobileMenu: { position: 'absolute', top: '100%', left: '-0.5rem', right: '-0.5rem', marginTop: '1rem', display: 'flex', flexDirection: 'column', padding: '1.5rem', borderRadius: '24px', gap: '1rem', zIndex: 1000 },
  mobileLink: { textAlign: 'left', padding: '0.8rem', fontSize: '1.2rem', fontWeight: '900', color: 'var(--text-primary)', width: '100%' },
  profileBox: { cursor: 'pointer', marginLeft: '0.5rem' },
  navAvatar: { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)', padding: '2px' },
  avatarFallback: { width: '32px', height: '32px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900' },
  logoutBtn: { background: "rgba(239, 68, 68, 0.05)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.2)", padding: "0.5rem 1rem", borderRadius: "10px", fontSize: "0.75rem", fontWeight: "800", marginLeft: '0.5rem' }
};

export default Navbar;